/**
 * MessageStatus — Sent / Delivered / Read status icons for messages
 *
 * Shows read receipt indicators:
 *   - Clock icon = sending (pending)
 *   - Single grey check = sent / delivered (message arrived)
 *   - Single blue check = read
 *   - Red X circle = failed
 *
 * Adapted from bachata-nation-next MessageStatus.tsx.
 */

import { memo } from 'react';

export type MessageDeliveryStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

interface MessageStatusProps {
  /** Unified status prop — preferred API */
  status?: MessageDeliveryStatus;
  /** @deprecated Use `status` instead */
  isPending?: boolean;
  /** @deprecated Use `status` instead */
  isFailed?: boolean;
  /** @deprecated Use `status` instead */
  isRead?: boolean;
  /** @deprecated Use `status` instead */
  isDelivered?: boolean;
  className?: string;
}

/** Resolve legacy boolean props into a single status value */
function resolveStatus(props: MessageStatusProps): MessageDeliveryStatus {
  if (props.status) return props.status;
  if (props.isFailed) return 'failed';
  if (props.isPending) return 'sending';
  if (props.isRead) return 'read';
  if (props.isDelivered) return 'delivered';
  return 'sent';
}

const GREY = 'var(--color-neutral-300)';
const BLUE = '#3b82f6'; // blue-500

export const MessageStatus = memo(function MessageStatus(props: MessageStatusProps) {
  const { className = '' } = props;
  const status = resolveStatus(props);

  const base = `inline-block h-4 w-4 ${className}`;

  // Failed — red X in circle
  if (status === 'failed') {
    return (
      <svg
        className={`${base} text-error-500`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-label="Failed"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    );
  }

  // Sending — clock icon
  if (status === 'sending') {
    return (
      <svg
        className={base}
        viewBox="0 0 24 24"
        fill="none"
        stroke={GREY}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.4}
        aria-label="Sending"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    );
  }

  // Read — single check, blue
  if (status === 'read') {
    return (
      <svg
        className={base}
        viewBox="0 0 24 24"
        fill="none"
        stroke={BLUE}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-label="Read"
      >
        <polyline points="4 12 9 17 20 6" />
      </svg>
    );
  }

  // Sent / Delivered — single check, grey
  return (
    <svg
      className={base}
      viewBox="0 0 24 24"
      fill="none"
      stroke={GREY}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={0.4}
      aria-label={status === 'delivered' ? 'Delivered' : 'Sent'}
    >
      <polyline points="4 12 9 17 20 6" />
    </svg>
  );
});

export default MessageStatus;
